/**
 * Split Agreements Service
 * Manages revenue split agreements between contributors on works/releases.
 * Reads happen in the browser; every WRITE goes through /api/splits/action (lib/split-engine.ts).
 */

import { auth, db } from '@/lib/firebase';
import {
  collection, doc, getDoc, getDocs,
  query, where, orderBy, Timestamp, onSnapshot,
} from 'firebase/firestore';

// ═══════════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════════

/** pending = owner offered (waiting for the person) · proposed = the person claimed/countered
 *  (waiting for the owner) · accepted = both agreed · disputed = the person declined the offer */
export type SplitStatus = 'pending' | 'proposed' | 'accepted' | 'disputed';
export type AgreementStatus = 'draft' | 'active' | 'disputed' | 'frozen';

/**
 * Three-part music rights model:
 *
 *  writingPercent     — Share of the WRITING right (song composition).
 *                       All writers divide 100 % between them.
 *                       Publishers do NOT earn writing rights.
 *
 *  publishingPercent  — Share of the PUBLISHING right (exploitation/admin).
 *                       Writers AND publishers share this 100 %.
 *                       If there are no publishers, the writers take 100 %.
 *
 *  performancePercent — Share of the PERFORMANCE right (recorded performance).
 *                       Performers, session musicians, producers, DJs, etc.
 *                       divide 100 % between them.
 *
 *  Each pool independently sums to 100 % (or 0 % if that right is not used).
 *  Stored as plain % (0–100), NOT basis points, to keep the UI simple.
 *
 *  Legacy: `percent` is kept for backward compatibility only.
 */
export interface SplitParty {
  userId: string;
  userName: string;
  userAvatar?: string;
  role: string;

  // Three-part rights (each pool sums to 100 across all parties, or 0 if unused)
  writingPercent: number;      // 0–100
  publishingPercent: number;   // 0–100
  performancePercent: number;  // 0–100

  status: SplitStatus;
  acceptedAt?: Timestamp;

  /** Legacy single-pool percent (basis points). IGNORED for money — never set by the server. */
  percent?: number;

  /** Share of SALES in basis points (0–10000) — the money. Only counts once accepted. */
  salesShareBp?: number;
  roles?: string[];
  creditKind?: 'split' | 'thanks';
  /** Whose turn it is to answer (null = nothing open). */
  awaiting?: 'owner' | 'party' | null;
  /** A change to ACCEPTED terms that the other side has not agreed yet. */
  proposal?: (Omit<SplitParty, 'userId' | 'userName' | 'userAvatar' | 'status' | 'acceptedAt' | 'awaiting' | 'proposal'> & { by: 'owner' | 'party'; note?: string }) | null;
}

export interface SplitAgreement {
  id?: string;
  mediaId: string;          // workId or releaseId
  mediaType: 'work' | 'release';
  mediaTitle: string;
  mediaCoverUrl?: string;

  splits: SplitParty[];

  /** Whether publishers are credited on this work (affects publishing pool rules). */
  hasPublishers: boolean;

  /** Which right pools are in use for this work. */
  writingEnabled: boolean;
  publishingEnabled: boolean;
  performanceEnabled: boolean;

  /** Legacy: total basis-point percent (kept for backward compat). */
  totalPercent?: number;

  status: AgreementStatus;

  /** Owner of the work/release, resolved by the server. */
  ownerUserId?: string | null;
  /** True once the server manages this agreement (all writes via /api/splits/action). */
  serverVerified?: boolean;

  createdBy: string;
  createdByName: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;

  // Future: on-chain attestation
  onChainTxHash?: string;
  onChainNetwork?: string;
}

/** Validate that each enabled rights pool sums to ≤ 100 (allows partial agreements). */
export function validateRightsPools(splits: SplitParty[], opts: {
  writingEnabled: boolean;
  publishingEnabled: boolean;
  performanceEnabled: boolean;
}): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  const totalW = splits.reduce((s, p) => s + (p.writingPercent || 0), 0);
  const totalPub = splits.reduce((s, p) => s + (p.publishingPercent || 0), 0);
  const totalPerf = splits.reduce((s, p) => s + (p.performancePercent || 0), 0);

  if (opts.writingEnabled && totalW > 100.01) errors.push(`Writing rights total ${totalW.toFixed(1)}% — must not exceed 100%.`);
  if (opts.publishingEnabled && totalPub > 100.01) errors.push(`Publishing rights total ${totalPub.toFixed(1)}% — must not exceed 100%.`);
  if (opts.performanceEnabled && totalPerf > 100.01) errors.push(`Performance rights total ${totalPerf.toFixed(1)}% — must not exceed 100%.`);

  return { valid: errors.length === 0, errors };
}

export interface DistributionResult {
  totalGross: number;
  platformFee: number;
  distributions: {
    userId: string;
    userName: string;
    percent: number;
    amount: number;       // net amount in cents
    transactionId: string;
    status: 'completed' | 'failed' | 'skipped';
    reason?: string;
  }[];
}

// ═══════════════════════════════════════════════════════════════════════════════
// CRUD
// ═══════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// Writes — ALL go through /api/splits/action (lib/split-engine.ts). The browser can no
// longer write splitAgreements directly (firestore.rules): every share must be agreed by
// both sides, and the server decides who owns the work and who may act.
// ═══════════════════════════════════════════════════════════════════════════════

export interface SplitTermsInput {
  role: string;
  roles?: string[];
  writingPercent?: number;
  publishingPercent?: number;
  performancePercent?: number;
  /** Share of sales in basis points (0–10000). Omitted = credited, not paid (owner keeps 100%). */
  salesShareBp?: number;
  creditKind?: 'split' | 'thanks';
}

async function splitAction(body: Record<string, unknown>): Promise<{ agreementId: string }> {
  const user = auth.currentUser;
  if (!user) throw new Error('Please sign in first.');
  const token = await user.getIdToken();
  const res = await fetch('/api/splits/action', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Could not update the agreement (${res.status})`);
  return { agreementId: String(data.agreementId || '') };
}

const partyBody = (party: Omit<SplitParty, 'status' | 'acceptedAt'>) => ({
  userId: party.userId,
  userName: party.userName,
  userAvatar: party.userAvatar || '',
  role: party.role,
  roles: (party as any).roles,
  writingPercent: party.writingPercent,
  publishingPercent: party.publishingPercent,
  performancePercent: party.performancePercent,
  salesShareBp: (party as any).salesShareBp,
  creditKind: (party as any).creditKind,
});

/** Create an agreement by proposing each party (kept for API compatibility). */
export async function createSplitAgreement(
  data: Omit<SplitAgreement, 'id' | 'createdAt' | 'updatedAt' | 'status'>,
): Promise<string> {
  let agreementId = '';
  for (const party of data.splits || []) {
    const out = await splitAction({
      action: 'propose',
      ...(agreementId ? { agreementId } : { media: { id: data.mediaId, type: data.mediaType, title: data.mediaTitle, coverUrl: data.mediaCoverUrl || '' } }),
      party: partyBody(party),
      poolFlags: { hasPublishers: data.hasPublishers, writingEnabled: data.writingEnabled, publishingEnabled: data.publishingEnabled, performanceEnabled: data.performanceEnabled },
    });
    agreementId = out.agreementId;
  }
  return agreementId;
}

/**
 * Owner offers terms to a person (creates the agreement on first use). The person must
 * accept, decline or counter before anything is in force.
 */
export async function addParticipantToAgreement(
  mediaId: string,
  mediaType: 'work' | 'release',
  mediaTitle: string,
  mediaCoverUrl: string,
  party: Omit<SplitParty, 'status' | 'acceptedAt'>,
  opts: {
    hasPublishers: boolean;
    writingEnabled: boolean;
    publishingEnabled: boolean;
    performanceEnabled: boolean;
    createdBy: string;
    createdByName: string;
  }
): Promise<{ agreementId: string; created: boolean }> {
  const existing = await getAgreementForMedia(mediaId, mediaType);
  const out = await splitAction({
    action: 'propose',
    media: { id: mediaId, type: mediaType, title: mediaTitle, coverUrl: mediaCoverUrl || '' },
    party: partyBody(party),
    poolFlags: { hasPublishers: opts.hasPublishers, writingEnabled: opts.writingEnabled, publishingEnabled: opts.publishingEnabled, performanceEnabled: opts.performanceEnabled },
  });
  return { agreementId: out.agreementId, created: !existing };
}

/** Claim a role / share on someone else's work. The owner accepts, declines or counters. */
export async function claimSplit(mediaId: string, mediaType: 'work' | 'release', mediaTitle: string, terms: SplitTermsInput, note?: string): Promise<string> {
  return (await splitAction({ action: 'claim', media: { id: mediaId, type: mediaType, title: mediaTitle }, terms, note })).agreementId;
}

/** Answer with different terms. Whoever's turn it is may counter; the turn passes to the other side. */
export async function counterSplit(agreementId: string, partyUserId: string, terms: SplitTermsInput, note?: string): Promise<void> {
  await splitAction({ action: 'counter', agreementId, partyUserId, terms, note });
}

/** Take back your own open proposal. */
export async function withdrawSplit(agreementId: string, partyUserId: string): Promise<void> {
  await splitAction({ action: 'withdraw', agreementId, partyUserId });
}

/**
 * Remove a participant from an agreement.
 * Only the creator of the agreement or the participant themselves can do this.
 */
export async function removeParticipantFromAgreement(
  agreementId: string,
  userId: string,
): Promise<void> {
  // The person may always leave; the owner may only remove shares that were never agreed.
  await splitAction({ action: 'remove', agreementId, partyUserId: userId });
}

/** Get a split agreement by ID */
export async function getSplitAgreement(agreementId: string): Promise<SplitAgreement | null> {
  const snap = await getDoc(doc(db, 'splitAgreements', agreementId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as SplitAgreement;
}

/** Get the active split agreement for a work/release */
export async function getAgreementForMedia(
  mediaId: string,
  mediaType: 'work' | 'release' = 'work',
): Promise<SplitAgreement | null> {
  // Two equality filters only — NO orderBy in the query, so this never needs a
  // composite index (adding orderBy('createdAt') would). We sort client-side
  // instead. Without this, the query threw FAILED_PRECONDITION and every
  // add-participant attempt failed with "Failed to add participant to any track".
  const q = query(
    collection(db, 'splitAgreements'),
    where('mediaId', '==', mediaId),
    where('mediaType', '==', mediaType),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const millis = (v: any): number =>
    v?.toMillis?.() ?? (typeof v === 'string' ? Date.parse(v) || 0 : 0);
  // Most recent first (client-side sort avoids the composite-index requirement).
  const docs = snap.docs
    .map(d => ({ id: d.id, ...d.data() } as SplitAgreement))
    .sort((a, b) => millis((b as any).createdAt) - millis((a as any).createdAt));
  return docs[0];
}

/** Get all agreements a user is part of */
export async function getAgreementsForUser(userId: string): Promise<SplitAgreement[]> {
  // Firestore doesn't support array-contains on nested objects,
  // so we query all and filter client-side (or use a denormalized memberIds array)
  const q = query(
    collection(db, 'splitAgreements'),
    orderBy('createdAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() } as SplitAgreement))
    .filter(a => a.splits.some(s => s.userId === userId));
}

/** Accept a split (contributor confirms their share) */
export async function acceptSplit(
  agreementId: string,
  userId: string,
): Promise<void> {
  // `userId` is the party whose terms are being accepted. The server checks it is the
  // caller's turn (the person for an owner offer, the owner for a claim/counter).
  await splitAction({ action: 'accept', agreementId, partyUserId: userId });
}

/** Dispute a split */
export async function disputeSplit(
  agreementId: string,
  userId: string,
  reason: string,
): Promise<void> {
  await splitAction({ action: 'decline', agreementId, partyUserId: userId, note: reason });
}

/** Update splits (reset all statuses to pending so parties must re-accept). */
export async function updateSplits(
  agreementId: string,
  newSplits: Omit<SplitParty, 'status' | 'acceptedAt'>[],
): Promise<void> {
  // Each changed person gets a proposal they must accept; agreed terms of everyone else
  // stay in force (previously every edit reset ALL parties to pending).
  const current = await getSplitAgreement(agreementId);
  if (!current) throw new Error('Agreement not found');
  const key = (p: any) => JSON.stringify([p.role, p.writingPercent || 0, p.publishingPercent || 0, p.performancePercent || 0, p.salesShareBp || 0, p.creditKind || 'split']);
  for (const next of newSplits) {
    const prev = current.splits.find(p => p.userId === next.userId);
    if (!prev || key(prev) !== key(next)) {
      await splitAction({ action: 'propose', agreementId, party: partyBody(next) });
    }
  }
  for (const prev of current.splits) {
    if (!newSplits.some(n => n.userId === prev.userId)) {
      await splitAction({ action: 'remove', agreementId, partyUserId: prev.userId });
    }
  }
}

/** Freeze an agreement (admin action — stops all distributions) */
export async function freezeAgreement(agreementId: string): Promise<void> {
  await splitAction({ action: 'freeze', agreementId });
}

// ═══════════════════════════════════════════════════════════════════════════════
// Revenue Distribution
// ═══════════════════════════════════════════════════════════════════════════════

// distributeSale() was removed: it was never called, and money is now handled
// server-side by lib/payout-ledger.ts (idempotent, tested).

// ═══════════════════════════════════════════════════════════════════════════════
// Real-time Subscriptions
// ═══════════════════════════════════════════════════════════════════════════════

/** Subscribe to agreement changes for a media item */
export function subscribeAgreement(
  mediaId: string,
  callback: (agreement: SplitAgreement | null) => void,
): () => void {
  // No orderBy → only a single-field `where` filter, which needs NO composite
  // index (the missing mediaId+createdAt index was making this query error and
  // spin forever). Sort newest-first client-side instead.
  const q = query(
    collection(db, 'splitAgreements'),
    where('mediaId', '==', mediaId),
  );
  return onSnapshot(
    q,
    (snap) => {
      if (snap.empty) { callback(null); return; }
      const docs = [...snap.docs].sort(
        (a, b) => ((b.data().createdAt?.toMillis?.() || 0) - (a.data().createdAt?.toMillis?.() || 0)),
      );
      callback({ id: docs[0].id, ...docs[0].data() } as SplitAgreement);
    },
    (err) => {
      // Without this handler a failed read (missing composite index, or rules
      // blocking public/logged-out viewers) left the panel spinning forever.
      // Resolve to null so the section renders its empty state instead.
      console.warn('[subscribeAgreement] read failed:', (err as any)?.message || err);
      callback(null);
    },
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════════

/** Convert percentage (0-100) to basis points (0-10000) */
export function percentToBasisPoints(percent: number): number {
  return Math.round(percent * 100);
}

/** Convert basis points (0-10000) to percentage (0-100) */
export function basisPointsToPercent(bp: number): number {
  return bp / 100;
}

/** Format basis points as display string */
export function formatSplitPercent(bp: number): string {
  const pct = bp / 100;
  return pct % 1 === 0 ? `${pct}%` : `${pct.toFixed(2)}%`;
}
